import React, { useState } from "react";
import api from "@/lib/api";
import { Shield } from "@/components/Logo";
import { useBranding } from "@/context/BrandingContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { CATALOG } from "@/lib/roofCatalog";
import { ROOF_TYPES, ROOF_TYPE_LABEL, ROOF_TYPE_CAT, ROOF_TYPE_DESC, ROOF_TYPE_SUB, ROOF_TYPE_TONE, tLabel } from "@/lib/roofTypes";
import { usd } from "@/lib/format";
import { Zap, Loader2, Check, ArrowRight, ArrowLeft, Home, CreditCard, MapPin, ShieldCheck, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = 120;
const perMo = (cents) => Math.max(1, Math.round(cents / 100 / MONTHS));

// --- Satellite roof-outline overlay (Google Static Maps + Web Mercator projection) ---
const SM_ZOOM = 20, SM_W = 640, SM_H = 420;
const smProject = (lat, lon, z) => {
  const s = 256 * 2 ** z;
  const x = ((lon + 180) / 360) * s;
  const sinv = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinv) / (1 - sinv)) / (4 * Math.PI)) * s;
  return { x, y };
};
function RoofOutline({ center, boxes, apiBase, address, squares }) {
  const c = smProject(center.lat, center.lon, SM_ZOOM);
  const toPx = (lat, lon) => { const p = smProject(lat, lon, SM_ZOOM); return { x: SM_W / 2 + (p.x - c.x), y: SM_H / 2 + (p.y - c.y) }; };
  const url = `${apiBase}/aerial?lat=${center.lat}&lon=${center.lon}&zoom=${SM_ZOOM}`;
  return (
    <div className="mb-card overflow-hidden relative" style={{ aspectRatio: `${SM_W}/${SM_H}` }} data-testid="roof-outline">
      <img src={url} alt="Your roof outlined from above" className="absolute inset-0 w-full h-full object-cover" />
      <svg viewBox={`0 0 ${SM_W} ${SM_H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
        {boxes.map((b, i) => {
          const ne = toPx(b.ne.latitude, b.ne.longitude);
          const sw = toPx(b.sw.latitude, b.sw.longitude);
          const x = Math.min(ne.x, sw.x), y = Math.min(ne.y, sw.y);
          const w = Math.abs(ne.x - sw.x), h = Math.abs(ne.y - sw.y);
          return <rect key={i} x={x} y={y} width={w} height={h} fill="rgba(200,16,46,0.18)" stroke="#C8102E" strokeWidth="2" rx="2" />;
        })}
      </svg>
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 flex items-end justify-between">
        <div className="text-white text-sm flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brandred" /> {address}</div>
        <div className="bg-white/90 text-navy rounded-lg px-3 py-1.5 text-xs font-bold">{squares} squares · {boxes.length} facets</div>
      </div>
    </div>
  );
}

export default function PublicInstaQuote() {
  const [stage, setStage] = useState("address"); // address | details | result
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "", squares: "", pitch: "6/12" });
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tier, setTier] = useState("SHINGLE");
  const [pick, setPick] = useState({ brand: "", product: "", colorName: "" });
  const [viz, setViz] = useState({ key: "", image: null, loading: false });
  const [viewMode, setViewMode] = useState("aerial"); // aerial | street
  const [going, setGoing] = useState(false);
  const { branding } = useBranding();

  const isSolar = result?.measurement_source === "Google Solar";
  const measureLine = isSolar
    ? `Measured from satellite: ${result?.squares} squares · ${result?.pitch} predominant pitch · ${result?.segments} roof facet${result?.segments === 1 ? "" : "s"}`
    : `Estimated roof size: ~${result?.squares ?? ""} squares`;

  const cat = ROOF_TYPE_CAT[tier] || "Shingle";
  const brands = Object.keys(CATALOG[cat] || {});
  const bBrand = brands.includes(pick.brand) ? pick.brand : brands[0];
  const products = Object.keys((CATALOG[cat] || {})[bBrand] || {});
  const bProduct = products.includes(pick.product) ? pick.product : products[0];
  const swatches = ((CATALOG[cat] || {})[bBrand] || {})[bProduct] || [];
  const bColor = swatches.find((s) => s.name === pick.colorName) || swatches[0];

  const defaultsFor = (t) => {
    const cc = ROOF_TYPE_CAT[t] || "Shingle"; const bb = Object.keys(CATALOG[cc])[0];
    const pp = Object.keys(CATALOG[cc][bb])[0]; const co = CATALOG[cc][bb][pp][0];
    return { brand: bb, product: pp, colorName: co.name };
  };
  const selectTier = (t) => { setTier(t); setPick(defaultsFor(t)); };

  const aerialUrl = coords ? `${API_BASE}/aerial?lat=${coords.lat}&lon=${coords.lon}` : (f.address ? `${API_BASE}/aerial?address=${encodeURIComponent(f.address)}` : null);
  const streetUrl = (coords || f.address) ? `${API_BASE}/streetview?${coords ? `lat=${coords.lat}&lon=${coords.lon}&` : ""}address=${encodeURIComponent(f.address || "")}` : null;
  const baseUrl = viewMode === "street" ? streetUrl : aerialUrl;

  const vizKey = `${viewMode}|${bBrand}|${bProduct}|${bColor?.name}`;
  const previewRoof = async () => {
    if (!baseUrl) return toast.error("Pick an address first");
    if (viz.key === vizKey && viz.image) return;
    setViz({ key: vizKey, image: null, loading: true });
    try {
      const resp = await fetch(baseUrl);
      if (!resp.ok) throw new Error("base image failed");
      const blob = await resp.blob();
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
      const { data } = await api.post("/quote/visualize", { image_base64: dataUrl, material_name: `${bBrand} ${bProduct}`, color: bColor?.name, color_hex: bColor?.hex, view: viewMode });
      setViz({ key: vizKey, image: data.image, loading: false });
    } catch { setViz({ key: "", image: null, loading: false }); toast.error(viewMode === "street" ? "No street view for this address — try Aerial" : "Could not render preview — try again"); }
  };

  const shareRoof = async () => {
    if (!viz.image) return;
    try {
      const blob = await (await fetch(viz.image)).blob();
      const file = new File([blob], "my-new-roof.png", { type: blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My New Roof", text: `My new ${bBrand} ${bProduct} roof in ${bColor?.name} — ${branding.company_name}` });
      } else {
        const a = document.createElement("a"); a.href = viz.image; a.download = "my-new-roof.png"; document.body.appendChild(a); a.click(); a.remove();
        toast.success("Saved to your device");
      }
    } catch { toast.error("Could not share right now"); }
  };

  const goDeposit = async () => {
    setGoing(true);
    try {
      const { data } = await api.post("/instaquote/proposal", { estimate_id: result.estimate_id, tier, brand: bBrand, product: bProduct, color: bColor?.name, color_hex: bColor?.hex });
      window.location.href = `/proposal/${data.proposal_id}`;
    } catch { toast.error("Could not start checkout"); setGoing(false); }
  };

  const submit = async () => {
    if (!f.name.trim() || !f.address.trim()) return toast.error("Name and address are required");
    setBusy(true);
    try {
      const { data } = await api.post("/instaquote", { ...f, squares: f.squares ? Number(f.squares) : null, lat: coords?.lat ?? null, lon: coords?.lon ?? null });
      setResult(data);
      setTier("SHINGLE"); setPick(defaultsFor("SHINGLE"));
      setStage("result");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not build your quote"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #C8102E 0, transparent 40%), radial-gradient(circle at 80% 0%, #fff 0, transparent 35%)" }} />
      <div className="max-w-4xl mx-auto px-4 py-10 relative">
        <div className="flex justify-center mb-8"><Shield size={48} withText /></div>

        {/* STAGE 1 — ADDRESS */}
        {stage === "address" && (
          <div className="text-center animate-scale-in" data-testid="iq-stage-address">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide mb-6"><Zap className="w-3.5 h-3.5 text-brandred" /> Instant Roof Quote</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-none">Your new roof price<br /><span className="text-brandred">in 60 seconds</span></h1>
            <p className="text-slate-300 mt-4 max-w-lg mx-auto">Type your address — we'll pull up your roof from the sky and price it instantly. No phone call, no waiting.</p>
            <div className="mb-card bg-white text-slate-800 p-2 max-w-xl mx-auto mt-8 flex flex-col sm:flex-row gap-2 items-stretch">
              <div className="flex-1 flex items-center gap-2 px-3">
                <MapPin className="w-5 h-5 text-brandred shrink-0" />
                <AddressAutocomplete testid="iq-address" value={f.address}
                  onChange={(v) => setF({ ...f, address: v })}
                  onSelect={(p) => { setF((s) => ({ ...s, address: p.display_name })); setCoords({ lat: p.lat, lon: p.lon }); }}
                  placeholder="Enter your home address…"
                  className="w-full py-3 text-sm outline-none border-0" />
              </div>
              <button data-testid="iq-get-started" onClick={() => { if (!f.address.trim()) return toast.error("Enter your address"); setStage("details"); }}
                className="bg-brandred hover:bg-brandred-dark text-white font-bold px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-colors whitespace-nowrap">
                See my roof <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-slate-300 text-xs">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-successg" /> Free & instant</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-successg" /> No obligation</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-successg" /> Licensed & insured</span>
            </div>
          </div>
        )}

        {/* STAGE 2 — DETAILS + AERIAL */}
        {stage === "details" && (
          <div className="animate-scale-in" data-testid="iq-stage-details">
            <button data-testid="iq-back" onClick={() => setStage("address")} className="text-slate-300 text-sm flex items-center gap-1 mb-4 hover:text-white"><ArrowLeft className="w-4 h-4" /> Change address</button>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="mb-card overflow-hidden bg-white/5 border border-white/10">
                {aerialUrl ? <img data-testid="iq-aerial" src={aerialUrl} alt="Your roof from above" className="w-full h-64 md:h-full object-cover" /> : <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Aerial preview unavailable</div>}
                <div className="p-3 text-xs text-slate-300 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brandred" /> {f.address}</div>
              </div>
              <div className="mb-card bg-white text-slate-800 p-5">
                <h2 className="font-display text-2xl font-extrabold uppercase text-navy">Almost there</h2>
                <p className="text-sm text-slate-500 mb-4">Tell us where to send your price. We auto-measure your roof from the aerial — leave size blank unless you know it.</p>
                <div className="space-y-3">
                  <div><label className="text-xs font-semibold uppercase text-slate-500">Full Name *</label><input data-testid="iq-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-semibold uppercase text-slate-500">Phone</label><input data-testid="iq-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" /></div>
                    <div><label className="text-xs font-semibold uppercase text-slate-500">Email</label><input data-testid="iq-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" /></div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-slate-500">Roof Size (sq, optional)</label>
                    <input data-testid="iq-squares" type="number" value={f.squares} onChange={(e) => setF({ ...f, squares: e.target.value })} placeholder="Auto — measured from satellite" className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-num outline-none focus:border-navy" />
                  </div>
                  <button data-testid="iq-submit" onClick={submit} disabled={busy} className="w-full bg-brandred hover:bg-brandred-dark text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Get My Instant Price
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STAGE 3 — RESULT */}
        {stage === "result" && result && (
          <div className="animate-scale-in" data-testid="instaquote-result">
            {(result.bounding_boxes?.length > 0 && result.center) ? (
              <div className="mb-6">
                <RoofOutline center={result.center} boxes={result.bounding_boxes} apiBase={API_BASE} address={f.address} squares={result.squares} />
              </div>
            ) : aerialUrl ? (
              <div className="mb-card overflow-hidden mb-6 relative">
                <img src={aerialUrl} alt="Your roof" className="w-full h-48 object-cover" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 flex items-end justify-between">
                  <div className="text-white text-sm flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brandred" /> {f.address}</div>
                  <div className="bg-white/90 text-navy rounded-lg px-3 py-1.5 text-xs font-bold">~{result.squares} squares</div>
                </div>
              </div>
            ) : null}
            <div className="text-center mb-6">
              <h2 className="font-display text-3xl font-extrabold uppercase">Your Roof Quote</h2>
              <p data-testid="iq-measure-line" className="text-sm mt-1 font-semibold text-brandred">{measureLine}</p>
              <p className="text-slate-300 text-xs mt-1">Pick your roof type. All prices include a standard 15% waste factor.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {ROOF_TYPES.map((t) => {
                const v = result.variants[t];
                if (!v) return null;
                return (
                  <button key={t} data-testid={`iq-tier-${t}`} onClick={() => selectTier(t)}
                    className={`text-left rounded-2xl p-5 border-2 transition-all bg-white text-slate-800 relative ${tier === t ? "border-brandred shadow-xl -translate-y-1" : "border-transparent opacity-90 hover:opacity-100"}`}>
                    {t === "SHINGLE" && <span className="absolute -top-2.5 right-4 bg-brandred text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Popular</span>}
                    <span className={`inline-block ${ROOF_TYPE_TONE[t]} text-white text-[11px] font-bold uppercase px-2.5 py-1 rounded-full`}>{ROOF_TYPE_LABEL[t]}</span>
                    <div className="font-num text-3xl font-extrabold text-navy mt-3">{usd(v.total_cents)}</div>
                    <div className="text-xs font-semibold text-brandred mt-1">or ${perMo(v.total_cents)}/mo*</div>
                    <div className="text-sm font-semibold text-navy mt-2">{ROOF_TYPE_DESC[t]}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{ROOF_TYPE_SUB[t]}</div>
                  </button>
                );
              })}
            </div>

            <div className="mb-card bg-white text-slate-800 p-5 mb-4" data-testid="tier-detail">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-xl font-extrabold uppercase text-navy">Customize your {tLabel(tier).toLowerCase()} roof</h3>
                <span className="text-xs font-semibold text-slate-400">{tLabel(tier)} system</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Brand</label>
                  <select data-testid="tier-brand" value={bBrand} onChange={(e) => { const nb = e.target.value; const np = Object.keys(CATALOG[cat][nb])[0]; const nc = CATALOG[cat][nb][np][0]; setPick({ brand: nb, product: np, colorName: nc.name }); }} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Roof Type / Product</label>
                  <select data-testid="tier-product" value={bProduct} onChange={(e) => { const np = e.target.value; const nc = CATALOG[cat][bBrand][np][0]; setPick({ brand: bBrand, product: np, colorName: nc.name }); }} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
                    {products.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase text-slate-500">Color — <span className="text-navy font-bold">{bColor?.name}</span></label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="tier-colors">
                  {swatches.map((s) => (
                    <button key={s.name} data-testid={`swatch-${s.name}`} title={s.name} onClick={() => setPick({ brand: bBrand, product: bProduct, colorName: s.name })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${bColor?.name === s.name ? "border-brandred ring-2 ring-brandred/30 scale-110" : "border-slate-200 hover:border-slate-400"}`} style={{ backgroundColor: s.hex }} />
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                <div className="w-12 h-12 rounded-lg border border-slate-200 shrink-0" style={{ backgroundColor: bColor?.hex }} />
                <div className="text-sm"><div className="font-bold text-navy">{bBrand} {bProduct}</div><div className="text-slate-500 text-xs">Color: {bColor?.name}</div></div>
              </div>
              {(coords || f.address) && (
                <div className="mt-4">
                  {branding.has_google && (
                    <div className="inline-flex rounded-xl border-2 border-slate-200 p-0.5 mb-2" data-testid="view-toggle">
                      <button data-testid="view-aerial" onClick={() => setViewMode("aerial")} className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${viewMode === "aerial" ? "bg-navy text-white" : "text-slate-500"}`}>Aerial</button>
                      <button data-testid="view-street" onClick={() => setViewMode("street")} className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${viewMode === "street" ? "bg-navy text-white" : "text-slate-500"}`}>Street view</button>
                    </div>
                  )}
                  <button data-testid="preview-roof-btn" onClick={previewRoof} disabled={viz.loading}
                    className="w-full bg-navy text-white font-bold py-2.5 rounded-xl border-2 border-navy flex items-center justify-center gap-2 disabled:opacity-60">
                    {viz.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering your roof…</> : <><Sparkles className="w-4 h-4" /> See my roof {viewMode === "street" ? "from the street" : "in this color"}</>}
                  </button>
                  {viz.key === vizKey && viz.image && (
                    <>
                      <div className="grid grid-cols-2 gap-2 mt-3" data-testid="roof-preview">
                        <div><div className="text-[11px] font-semibold uppercase text-slate-400 mb-1">Now</div><img src={baseUrl} alt="current roof" className="w-full h-40 object-cover rounded-lg border border-slate-200" /></div>
                        <div><div className="text-[11px] font-semibold uppercase text-brandred mb-1">{bColor?.name}</div><img src={viz.image} alt="new roof preview" className="w-full h-40 object-cover rounded-lg border border-slate-200" /></div>
                      </div>
                      <button data-testid="share-roof-btn" onClick={shareRoof} className="w-full mt-2 bg-white text-navy font-bold py-2.5 rounded-xl border-2 border-navy flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
                        <Share2 className="w-4 h-4" /> Share / Save my roof
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="mb-card bg-white text-slate-800 p-5 text-center">
              <p className="text-sm text-slate-600 mb-3">You picked <b className="text-navy">{tLabel(tier)}</b> — {bBrand} {bProduct} in <b className="text-navy">{bColor?.name}</b> · {usd(result.variants[tier].total_cents)} (about <b className="text-brandred">${perMo(result.variants[tier].total_cents)}/mo</b>). Lock it in now, or we'll follow up.</p>
              <button onClick={goDeposit} disabled={going} data-testid="iq-deposit-btn"
                className="w-full sm:w-auto mx-auto bg-brandred hover:bg-brandred-dark text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {going ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay Deposit & E-Sign
              </button>
              <p className="text-xs text-slate-400 flex items-center justify-center gap-1 mt-3"><Home className="w-3.5 h-3.5" /> Or a {branding.company_name} specialist will reach out to confirm measurements.</p>
              <a href="/" className="inline-flex items-center gap-1 text-navy font-semibold text-sm mt-2 hover:text-brandred">Done for now <ArrowRight className="w-4 h-4" /></a>
            </div>
            <p className="text-center text-[11px] text-slate-400 mt-3">*Estimated monthly payment over {MONTHS} months with approved financing. Not a financing offer.</p>
          </div>
        )}

        <p className="text-center text-[11px] text-slate-400 mt-8" data-testid="iq-brand-footer">Powered by {branding.company_name}{branding.phone ? ` · ${branding.phone}` : ""}{branding.website ? ` · ${branding.website}` : ""}{branding.license ? ` · License ${branding.license}` : ""} · Instant estimates are approximate and confirmed before work begins.</p>
      </div>
    </div>
  );
}
